import {
  MAX_SLIDES,
  QUOTE_MAX_CHARS,
  type QuoteCarousel,
  type QuoteCarouselFormat,
  type QuoteFrameSource,
  type QuoteSlide,
  type QuoteSlideBackground,
  type QuoteSlideStyle,
} from './types';
import { sanitizeBackgroundFrame, sanitizeTextPosition } from './slidePlacement';
import { sanitizeQuoteTextStyle } from './quoteTextStyle';

const VALID_STYLES: readonly QuoteSlideStyle[] = ['card', 'frame'];
const VALID_FRAME_SOURCES: readonly QuoteFrameSource[] = ['original', 'edited'];
const VALID_FORMATS: readonly QuoteCarouselFormat[] = ['portrait_4x5', 'portrait_9x16'];
const HEX_COLOR_RE = /^#[0-9a-f]{6}$/i;
const MAX_HANDLE_CHARS = 40;

function isValidStyle(value: unknown): value is QuoteSlideStyle {
  return typeof value === 'string' && (VALID_STYLES as readonly string[]).includes(value);
}

function isValidFormat(value: unknown): value is QuoteCarouselFormat {
  return typeof value === 'string' && (VALID_FORMATS as readonly string[]).includes(value);
}

function isValidFrameSource(value: unknown): value is QuoteFrameSource {
  return typeof value === 'string' && (VALID_FRAME_SOURCES as readonly string[]).includes(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function sanitizeBackground(raw: unknown): QuoteSlideBackground | null {
  if (raw === null || typeof raw !== 'object') return null;
  const candidate = raw as Record<string, unknown>;

  if (candidate.kind === 'frame') {
    const url = typeof candidate.url === 'string' && candidate.url.startsWith('https://')
      ? candidate.url
      : null;
    // Drafts saved before the original/edited choice have neither field:
    // they were automatic captures from the original cut.
    const source = isValidFrameSource(candidate.source) ? candidate.source : 'original';
    const time = isFiniteNumber(candidate.time) && candidate.time >= 0 ? candidate.time : null;
    return {
      kind: 'frame', url, source, time,
    };
  }

  if (candidate.kind === 'color') {
    if (typeof candidate.color === 'string' && HEX_COLOR_RE.test(candidate.color)) {
      return { kind: 'color', color: candidate.color };
    }
    return null;
  }

  return null;
}

function sanitizeSlide(raw: unknown): QuoteSlide | null {
  if (raw === null || typeof raw !== 'object') return null;
  const candidate = raw as Record<string, unknown>;

  if (typeof candidate.id !== 'string' || candidate.id.length === 0) return null;

  const text = typeof candidate.text === 'string' ? candidate.text.trim() : '';
  if (text.length === 0) return null;

  const { start, end } = candidate;
  if (!isFiniteNumber(start) || !isFiniteNumber(end) || start > end) return null;

  const slide: QuoteSlide = {
    id: candidate.id,
    text: text.slice(0, QUOTE_MAX_CHARS),
    start,
    end,
    background: sanitizeBackground(candidate.background),
  };
  // Optional placement: absent on older drafts, dropped when malformed.
  const textPosition = sanitizeTextPosition(candidate.textPosition);
  if (textPosition) slide.textPosition = textPosition;
  const backgroundFrame = sanitizeBackgroundFrame(candidate.backgroundFrame);
  if (backgroundFrame) slide.backgroundFrame = backgroundFrame;
  return slide;
}

/**
 * Validates and normalizes an arbitrary value into a `QuoteCarousel`,
 * stripping unknown keys. Returns `null` when the payload is not a
 * recognizable v1 carousel at all.
 */
export function sanitizeQuoteCarousel(raw: unknown): QuoteCarousel | null {
  if (raw === null || typeof raw !== 'object') return null;
  const candidate = raw as Record<string, unknown>;

  if (candidate.version !== 1) return null;

  const style = isValidStyle(candidate.style) ? candidate.style : 'card';
  const format = isValidFormat(candidate.format) ? candidate.format : 'portrait_4x5';
  const frameSource = isValidFrameSource(candidate.frameSource) ? candidate.frameSource : 'original';
  const brandKitId = typeof candidate.brandKitId === 'string' && candidate.brandKitId.length > 0
    ? candidate.brandKitId
    : null;
  const handle = typeof candidate.handle === 'string'
    ? candidate.handle.slice(0, MAX_HANDLE_CHARS)
    : '';

  const rawSlides = Array.isArray(candidate.slides) ? candidate.slides : [];
  const slides: QuoteSlide[] = [];
  for (const rawSlide of rawSlides) {
    if (slides.length >= MAX_SLIDES) break;
    const slide = sanitizeSlide(rawSlide);
    if (slide) slides.push(slide);
  }

  const carousel: QuoteCarousel = {
    version: 1, style, format, frameSource, brandKitId, handle, slides,
  };
  const textStyle = sanitizeQuoteTextStyle(candidate.textStyle);
  if (textStyle) carousel.textStyle = textStyle;
  return carousel;
}

export function emptyQuoteCarousel(): QuoteCarousel {
  return {
    version: 1,
    // New carousels get a video-frame background on every slide.
    style: 'frame',
    format: 'portrait_4x5',
    frameSource: 'original',
    brandKitId: null,
    handle: '',
    slides: [],
  };
}
